/**
 * Page Component
 *
 * Handles parsing and formatting of page structures.
 * Similar to component structure: contains input interface, parser, and formatter.
 */

import { v4 as uuidv4 } from 'uuid'

import { IPage, PageNature, PageType } from '@makyoapp/makyo-sdk/models/AppBuilder.js'

import { formatComponents } from '../components/format-components'
import { parseAiComponents } from '../components/parse-components'
import type { AiComponentInput } from '../components/registry'
import { deepMerge, extractTextFromVariableText, parseToVariableText } from '../components/parser-utils'
import pageTemplate from './page.template.json'

/**
 * Simplified AI Page Input
 */
export interface AiPageInput {
  type: 'page'
  tableId?: string
  title: string | string[]
  icon?: string
  backgroundColor?: string
  components: AiComponentInput[]
  topBar?: {
    type?: 'default' | 'minimal' | 'none'
    showTitle?: boolean
  }
  bottomBar?: {
    type?: 'default' | 'none'
  }
  seo?: {
    title?: string
    description?: string
    keywords?: string
  }
}

/**
 * Create slug from text
 */
function createSlug(text: string, defaultSlug: string = ''): string {
  if (!text || typeof text !== 'string') {
    return defaultSlug
  }

  let preformatted = text.trimEnd() + (text.endsWith(' ') ? '-' : '')
  const slug = preformatted
    .toLowerCase()
    .normalize('NFD')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '')

  return slug || defaultSlug
}

/**
 * Extract icon name from icon format string
 * Format: "icon:rocket_launch" -> "rocket_launch"
 * If no prefix, returns as-is
 */
function extractIconName(icon: string | undefined): string {
  if (!icon) return 'draft'
  if (icon.startsWith('icon:')) {
    return icon.substring(5) // Remove "icon:" prefix
  }
  return icon
}

/**
 * Format icon name and format it
 * Converts icon name to format: "icon:rocket_launch" or returns as-is
 */
function formatIconName(icon: { value: string } | undefined): string | undefined {
  if (!icon || !icon.value) return undefined
  const iconName = icon.value
  // If it doesn't start with "icon:", add the prefix
  if (!iconName.startsWith('icon:')) {
    return `icon:${iconName}`
  }
  return iconName
}

/**
 * Format page title from VariableTextValue or string
 */
function formatTitle(title: string | undefined): string | string[] {
  if (!title) return ''

  try {
    // Try to parse as JSON (VariableTextValue)
    const parsed = JSON.parse(title)
    if (Array.isArray(parsed)) {
      // It's a VariableTextValue array, extract text
      return extractTextFromVariableText(parsed)
    }
  } catch {
    // Not JSON, return as string
  }

  return title
}

const PAGE_DEFAULT_TEMPLATE = pageTemplate as unknown as IPage

/**
 * Parse AI page input to full page structure
 */
export function parseAiPage(input: AiPageInput): IPage {
  // Start with template defaults
  const result = deepMerge(PAGE_DEFAULT_TEMPLATE, {})

  // Generate new ID and slug
  result._id = uuidv4()

  // Parse title to VariableTextValue (same as button label)
  const titleVariableText = parseToVariableText(input.title)
  result._Settings._Title = JSON.stringify(titleVariableText)

  // Extract plain text for slug generation
  const titleText = extractTextFromVariableText(titleVariableText)
  result._Slug = createSlug(titleText)

  // Create _Body structure (nested) and _BodySettings (flat)
  const { bodies, bodySettings } = parseAiComponents(input.components)
  result._Body = bodies
  result._BodySettings = bodySettings

  // Override settings if provided
  if (input.tableId) {
    result._TableID = input.tableId
  }

  if (input.icon) {
    result._Settings._Icon = { value: extractIconName(input.icon) }
  }

  if (input.backgroundColor) {
    result._Settings._BackgroundColor = input.backgroundColor
  }

  // Set page type (template has empty string, but we want Standard)
  // PageType.Standard and PageNature.Standard are enum values, ensure they're set
  if (!result._Settings._Type) {
    result._Settings._Type = PageType.Standard
  }
  if (!result._Nature) {
    result._Nature = PageNature.Standard
  }

  // Override top bar if provided
  if (input.topBar) {
    if (input.topBar.type === 'minimal') {
      result._TopBar._Type = '1'
    } else if (input.topBar.type === 'none') {
      result._TopBar._Type = '2'
    }

    if (input.topBar.showTitle !== undefined) {
      result._TopBar._ShowPageTitle = input.topBar.showTitle
    }
  }

  // Override bottom bar if provided
  if (input.bottomBar && input.bottomBar.type === 'none') {
    result._BottomBar._Type = '1'
  }

  // Override SEO if provided
  if (input.seo) {
    if (input.seo.title) {
      result._SEO._MetaTitle = input.seo.title
    }
    if (input.seo.description) {
      result._SEO._MetaDescription = input.seo.description
    }
    if (input.seo.keywords) {
      result._SEO._MetaKeywords = input.seo.keywords
    }
  }

  return result
}

/**
 * Format IPage to AiPageInput
 */
export function formatAiPage(page: IPage): AiPageInput {
  const title = formatTitle(page._Settings?._Title)
  const icon = page._Settings?._Icon ? formatIconName({ value: page._Settings._Icon.value }) : undefined
  const backgroundColor = page._Settings?._BackgroundColor

  // Format components from _Body and _BodySettings
  const components = formatComponents(page._Body || [], page._BodySettings || {})

  // Format top bar
  const topBarType = page._TopBar?._Type
  const topBar: AiPageInput['topBar'] = {
    type: topBarType === '0' ? 'default' : topBarType === '1' ? 'minimal' : 'none',
    showTitle: page._TopBar?._ShowPageTitle ?? true,
  }

  // Format bottom bar
  const bottomBarType = page._BottomBar?._Type
  const bottomBar: AiPageInput['bottomBar'] = {
    type: bottomBarType === '0' ? 'default' : 'none',
  }

  // Format SEO
  const seo: AiPageInput['seo'] = {
    title: page._SEO?._MetaTitle || undefined,
    description: page._SEO?._MetaDescription || undefined,
    keywords: page._SEO?._MetaKeywords || undefined,
  }

  // Build result object with components last (for readability)
  // Collect optional properties first
  const optionalProps: Partial<AiPageInput> = {}
  
  if (page._TableID) {
    optionalProps.tableId = page._TableID
  }

  if (icon) {
    optionalProps.icon = icon
  }

  if (backgroundColor) {
    optionalProps.backgroundColor = backgroundColor
  }
  
  if (topBar.type !== 'default' || !topBar.showTitle) {
    optionalProps.topBar = topBar
  }
  if (bottomBar.type !== 'default') {
    optionalProps.bottomBar = bottomBar
  }
  if (seo.title || seo.description || seo.keywords) {
    optionalProps.seo = seo
  }

  // Build final object with components last (for readability, matching component container pattern)
  // Using object literal with explicit property order ensures JSON.stringify preserves order
  const result: AiPageInput = {
    type: 'page',
    title,
    ...optionalProps,
    components, // components comes last
  }

  return result
}
